import type { MessageInitShape } from '@bufbuild/protobuf';
import { getClient } from '@kentik/ui-shared/grpc/webClient';
import {
  type Correlation,
  type CorrelationFilterSchema,
  CorrelationStatus
} from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import type { ContextInput } from '@kentik/schema/gen/ts/kentik/alerting/context/v202602/input_pb';
import type {
  PaginationConfigSchema,
  SortingConfigSchema
} from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';

function client() {
  return getClient('CorrelationService');
}

export type CorrelationFilterInit = MessageInitShape<typeof CorrelationFilterSchema>;

export interface ListCorrelationsParams {
  filter?: CorrelationFilterInit;
  pagination?: MessageInitShape<typeof PaginationConfigSchema>;
  sorting?: MessageInitShape<typeof SortingConfigSchema>;
}

export async function listCorrelations(params: ListCorrelationsParams = {}) {
  return client().list(params);
}

export interface CorrelationWithEvents {
  correlation: Correlation | undefined;
  events: ContextInput[];
}

export async function getCorrelation(id: string): Promise<CorrelationWithEvents> {
  const response = await client().get({ id });
  return { correlation: response.correlation, events: response.events };
}

export async function updateCorrelation(params: {
  id: string;
  title?: string;
  summary?: string;
  close?: boolean;
}): Promise<Correlation | undefined> {
  const { id, title, summary, close } = params;
  const response = await client().update({
    id,
    title,
    summary,
    // Correlations are never manually reopened, so closing is the only status change.
    status: close ? CorrelationStatus.CLOSED : undefined
  });
  return response.correlation;
}

export async function removeAlertsFromCorrelation({
  id,
  alertIds
}: {
  id: string;
  alertIds: string[];
}): Promise<Correlation | undefined> {
  const response = await client().removeAlerts({ id, alertIds });
  return response.correlation;
}

export async function ackCorrelation(id: string): Promise<Correlation | undefined> {
  const response = await client().ack({ id });
  return response.correlation;
}

export async function unAckCorrelation(id: string): Promise<Correlation | undefined> {
  const response = await client().unAck({ id });
  return response.correlation;
}

export async function mergeCorrelations({
  sourceId,
  targetId
}: {
  sourceId: string;
  targetId: string;
}): Promise<Correlation | undefined> {
  const response = await client().merge({ sourceId, targetId });
  return response.correlation;
}

/** `notFoundIds` covers ids that were missing, not owned, or already closed. */
export interface BulkActionResult {
  appliedIds: string[];
  notFoundIds: string[];
}

export async function bulkAckCorrelations(ids: string[]): Promise<BulkActionResult> {
  const response = await client().bulkAck({ ids });
  return { appliedIds: response.ackedIds, notFoundIds: response.notFoundIds };
}

export async function bulkUnAckCorrelations(ids: string[]): Promise<BulkActionResult> {
  const response = await client().bulkUnAck({ ids });
  return { appliedIds: response.unackedIds, notFoundIds: response.notFoundIds };
}

export async function bulkCloseCorrelations(ids: string[]): Promise<BulkActionResult> {
  const response = await client().bulkClose({ ids });
  return { appliedIds: response.closedIds, notFoundIds: response.notFoundIds };
}

export interface CorrelationStatisticsParams {
  filter?: CorrelationFilterInit;
  bucket: string;
}

export async function getCorrelationStatistics(params: CorrelationStatisticsParams) {
  return client().getCorrelationStatistics(params);
}
